/**
 * Chain of 2+ links on the Three.js duel overlay: the "Chain" banner and the
 * pile of chain cards at mid-field, newest on top, each with its link number.
 * Poses are in Phaser world pixels and reprojected every frame, so the pile
 * stays on the mat when the compact camera pans or zooms. The scene decides
 * when cards join, step forward, and leave.
 */

import * as THREE from 'three'
import type { CardFoilId } from '@shared/index'

export interface ChainStackView {
  /** World point → overlay-normalized (0–1). */
  project: (x: number, y: number) => { x: number; y: number }
  /** Overlay-normalized size of a board card at scale 1. */
  cardSize: () => { w: number; h: number }
}

/** World pose. `scale` is relative to a board card. */
export interface ChainStackPose {
  x: number
  y: number
  scale: number
}

export interface ChainStackCard {
  uid: string
  link: number
  cardId: string
  backUrl?: string
  foil?: CardFoilId
}

export interface ChainCardMesh {
  group: THREE.Group
  frontMat: THREE.MeshBasicMaterial
  backMat: THREE.MeshBasicMaterial
  edgeMat: THREE.MeshBasicMaterial
  dispose: () => void
}

export interface ChainStackHost {
  readonly scene: THREE.Scene
  /** Overlay-normalized → Three.js pixels (y up). */
  toThree: (nx: number, ny: number) => { x: number; y: number }
  /** Overlay-normalized size → Three.js pixels. */
  pxSize: (nw: number, nh: number) => { w: number; h: number }
  /** Overlay size in pixels. */
  size: () => { w: number; h: number }
  makeCard: (
    cw: number,
    ch: number,
    cardId: string,
    backUrl?: string,
    foil?: CardFoilId,
  ) => ChainCardMesh
  reduced: () => boolean
}

const GOLD = 0xfde68a
const ASH = 0x9ca3af
const DIM_ALPHA = 0.4
const BASE_ORDER = 40

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3)
}

function easeOutBack(t: number): number {
  const c = 1.4
  const u = t - 1
  return 1 + (c + 1) * u * u * u + c * u * u
}

function linkTilt(link: number): number {
  return (link % 2 === 0 ? 1 : -1) * 0.045
}

function badgeTexture(link: number): THREE.CanvasTexture {
  const c = document.createElement('canvas')
  c.width = 96
  c.height = 96
  const ctx = c.getContext('2d')
  if (ctx) {
    const g = ctx.createRadialGradient(40, 36, 6, 48, 48, 46)
    g.addColorStop(0, '#fff7d6')
    g.addColorStop(0.55, '#f5c542')
    g.addColorStop(1, '#8a5a00')
    ctx.fillStyle = g
    ctx.beginPath()
    ctx.arc(48, 48, 44, 0, Math.PI * 2)
    ctx.fill()
    ctx.lineWidth = 5
    ctx.strokeStyle = '#2a1a00'
    ctx.stroke()
    ctx.fillStyle = '#1f1300'
    ctx.font = 'bold 54px Roboto, system-ui, sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(String(link), 48, 52)
  }
  const tex = new THREE.CanvasTexture(c)
  tex.colorSpace = THREE.SRGBColorSpace
  return tex
}

function glowTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas')
  c.width = 128
  c.height = 160
  const ctx = c.getContext('2d')
  if (ctx) {
    const g = ctx.createRadialGradient(64, 80, 10, 64, 80, 80)
    g.addColorStop(0, 'rgba(255,255,255,1)')
    g.addColorStop(0.55, 'rgba(255,255,255,0.55)')
    g.addColorStop(1, 'rgba(255,255,255,0)')
    ctx.fillStyle = g
    ctx.fillRect(0, 0, 128, 160)
  }
  return new THREE.CanvasTexture(c)
}

interface Tween {
  from: ChainStackPose
  to: ChainStackPose
  age: number
  delay: number
  dur: number
  back: boolean
}

interface Entry {
  card: ChainStackCard
  root: THREE.Group
  mesh: ChainCardMesh
  baseW: number
  baseH: number
  glowMat: THREE.MeshBasicMaterial
  glowGeo: THREE.PlaneGeometry
  badgeMat: THREE.MeshBasicMaterial
  badgeGeo: THREE.PlaneGeometry
  badgeTex: THREE.CanvasTexture
  badge: THREE.Mesh
  pose: ChainStackPose
  tween: Tween | null
  alpha: number
  /** Seconds left on the step-forward pulse. */
  focus: number
  focusDur: number
  fizzled: boolean
  /** Fading out; disposed at 0. */
  fade: number | null
  fadeDur: number
}

export class ChainStack {
  private readonly entries: Entry[] = []
  private glowTex: THREE.CanvasTexture | null = null
  private dimmed = false
  private dim = 1
  private time = 0

  constructor(
    private readonly host: ChainStackHost,
    private view: ChainStackView,
  ) {}

  setView(view: ChainStackView): void {
    this.view = view
  }

  has(uid: string): boolean {
    return this.entries.some((e) => e.card.uid === uid && e.fade === null)
  }

  isEmpty(): boolean {
    return this.entries.length === 0
  }

  /** Fly a card from its field slot into the pile. */
  add(card: ChainStackCard, from: ChainStackPose, to: ChainStackPose, seconds: number): void {
    if (this.has(card.uid)) {
      this.moveTo(card.uid, to, seconds)
      return
    }
    const px = this.cardPx()
    const mesh = this.host.makeCard(px.w, px.h, card.cardId, card.backUrl, card.foil)
    const root = new THREE.Group()
    const order = BASE_ORDER + card.link * 3
    // A Group's renderOrder outranks its children's. Root and card group share
    // one value so glow < card < badge is decided by the meshes' own orders.
    root.renderOrder = order

    if (!this.glowTex) this.glowTex = glowTexture()
    const glowGeo = new THREE.PlaneGeometry(px.w * 1.7, px.h * 1.45)
    const glowMat = new THREE.MeshBasicMaterial({
      map: this.glowTex,
      color: GOLD,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      depthTest: false,
      blending: THREE.AdditiveBlending,
    })
    const glow = new THREE.Mesh(glowGeo, glowMat)
    glow.renderOrder = order
    root.add(glow)

    mesh.group.traverse((o: THREE.Object3D) => {
      o.renderOrder = order + 1
    })
    mesh.group.renderOrder = order
    root.add(mesh.group)

    const badgeTex = badgeTexture(card.link)
    const badgeSize = Math.max(18, px.w * 0.42)
    const badgeGeo = new THREE.PlaneGeometry(badgeSize, badgeSize)
    const badgeMat = new THREE.MeshBasicMaterial({
      map: badgeTex,
      transparent: true,
      depthWrite: false,
      depthTest: false,
    })
    const badge = new THREE.Mesh(badgeGeo, badgeMat)
    badge.position.set(-px.w / 2 + badgeSize * 0.18, px.h / 2 - badgeSize * 0.18, 4)
    badge.renderOrder = order + 2
    badge.scale.setScalar(0)
    root.add(badge)

    this.host.scene.add(root)
    const entry: Entry = {
      card,
      root,
      mesh,
      baseW: px.w,
      baseH: px.h,
      glowMat,
      glowGeo,
      badgeMat,
      badgeGeo,
      badgeTex,
      badge,
      pose: { ...from },
      tween: { from: { ...from }, to: { ...to }, age: 0, delay: 0, dur: seconds, back: false },
      alpha: 1,
      focus: 0,
      focusDur: 0,
      fizzled: false,
      fade: null,
      fadeDur: 0,
    }
    this.entries.push(entry)
    this.place(entry)
  }

  moveTo(uid: string, to: ChainStackPose, seconds: number): void {
    const e = this.find(uid)
    if (!e) return
    e.tween = { from: { ...e.pose }, to: { ...to }, age: 0, delay: 0, dur: seconds, back: false }
  }

  /** The resolving link steps forward and pulses. A fizzled link turns grey. */
  focusOn(uid: string, to: ChainStackPose, seconds: number, fizzled: boolean): void {
    const e = this.find(uid)
    if (!e) return
    e.tween = {
      from: { ...e.pose },
      to: { ...to },
      age: 0,
      delay: 0,
      dur: Math.min(seconds * 0.45, 0.3),
      back: true,
    }
    e.focus = seconds
    e.focusDur = seconds
    e.fizzled = fizzled
    this.dimmed = true
  }

  /** Remove a card now and return where it was, so a flight can take over. */
  take(uid: string): ChainStackPose | null {
    const i = this.entries.findIndex((e) => e.card.uid === uid)
    if (i === -1) return null
    const e = this.entries[i]!
    const pose = { ...e.pose }
    this.entries.splice(i, 1)
    this.disposeEntry(e)
    return pose
  }

  /** The card left the field while waiting in the chain. */
  drop(uid: string, seconds: number): void {
    const e = this.find(uid)
    if (!e) return
    e.fade = seconds
    e.fadeDur = seconds
  }

  clear(seconds: number): void {
    for (const e of this.entries) {
      if (e.fade !== null) continue
      e.fade = seconds
      e.fadeDur = seconds
    }
    this.dimmed = false
  }

  /** False once the pile is empty; the host then drops this stack. */
  update(dt: number): boolean {
    this.time += dt
    const dimTarget = this.dimmed ? DIM_ALPHA : 1
    this.dim = lerp(this.dim, dimTarget, clamp01(dt * 8))
    for (let i = this.entries.length - 1; i >= 0; i--) {
      const e = this.entries[i]!
      if (e.fade !== null) {
        e.fade -= dt
        if (e.fade <= 0) {
          this.entries.splice(i, 1)
          this.disposeEntry(e)
          continue
        }
      }
      if (e.tween) {
        const tw = e.tween
        tw.age += dt
        const raw = clamp01((tw.age - tw.delay) / Math.max(0.01, tw.dur))
        const k = tw.back ? easeOutBack(raw) : easeOutCubic(raw)
        e.pose = {
          x: lerp(tw.from.x, tw.to.x, k),
          y: lerp(tw.from.y, tw.to.y, k),
          scale: lerp(tw.from.scale, tw.to.scale, k),
        }
        if (raw >= 1) e.tween = null
      }
      if (e.focus > 0) e.focus = Math.max(0, e.focus - dt)
      this.place(e)
    }
    return this.entries.length > 0
  }

  dispose(): void {
    for (const e of this.entries) this.disposeEntry(e)
    this.entries.length = 0
    this.glowTex?.dispose()
    this.glowTex = null
  }

  private find(uid: string): Entry | undefined {
    return this.entries.find((e) => e.card.uid === uid && e.fade === null)
  }

  private cardPx(): { w: number; h: number } {
    const n = this.view.cardSize()
    const px = this.host.pxSize(n.w, n.h)
    return { w: Math.max(8, px.w), h: Math.max(12, px.h) }
  }

  private place(e: Entry): void {
    const n = this.view.project(e.pose.x, e.pose.y)
    const p = this.host.toThree(n.x, n.y)
    const px = this.cardPx()
    const sizeMul = px.w / e.baseW
    const focused = e.focus > 0
    const focusT = focused ? 1 - e.focus / Math.max(0.01, e.focusDur) : 0
    const reduced = this.host.reduced()
    const bob = focused || reduced ? 0 : Math.sin(this.time * 2.2 + e.card.link) * 1.5
    e.root.position.set(p.x, p.y + bob, 10 + e.card.link)
    e.root.rotation.z = focused ? lerp(linkTilt(e.card.link), 0, clamp01(focusT * 4)) : linkTilt(e.card.link)
    e.root.scale.setScalar(e.pose.scale * sizeMul)

    const fade = e.fade !== null ? clamp01(e.fade / Math.max(0.01, e.fadeDur)) : 1
    const base = focused ? 1 : this.dim
    const alpha = base * fade
    e.mesh.frontMat.opacity = alpha
    e.mesh.backMat.opacity = alpha
    e.mesh.edgeMat.opacity = alpha
    e.mesh.frontMat.color.setHex(focused && e.fizzled ? ASH : 0xffffff)
    e.badgeMat.opacity = alpha

    const badgeAge = e.tween && !e.tween.back ? clamp01(e.tween.age / Math.max(0.01, e.tween.dur)) : 1
    const pop = badgeAge < 1 ? easeOutBack(clamp01((badgeAge - 0.5) * 2)) : 1
    const badgePulse = focused && !reduced ? 1 + Math.sin(focusT * Math.PI * 3) * 0.18 : 1
    e.badge.scale.setScalar(Math.max(0, pop) * badgePulse)

    let glow = 0
    if (focused) {
      const pulse = reduced ? 0.6 : 0.55 + Math.sin(focusT * Math.PI * 4) * 0.3
      glow = pulse * (1 - Math.max(0, focusT - 0.8) * 5)
    } else if (this.isTop(e)) {
      glow = 0.28 * this.dim
    }
    e.glowMat.opacity = Math.max(0, glow) * fade
    e.glowMat.color.setHex(focused && e.fizzled ? ASH : GOLD)
  }

  private isTop(e: Entry): boolean {
    let top: Entry | null = null
    for (const x of this.entries) {
      if (x.fade !== null) continue
      if (!top || x.card.link > top.card.link) top = x
    }
    return top === e
  }

  private disposeEntry(e: Entry): void {
    this.host.scene.remove(e.root)
    e.mesh.dispose()
    e.glowGeo.dispose()
    e.glowMat.dispose()
    e.badgeGeo.dispose()
    e.badgeMat.dispose()
    e.badgeTex.dispose()
  }
}

function bannerTexture(text: string): THREE.CanvasTexture {
  const c = document.createElement('canvas')
  c.width = 1024
  c.height = 192
  const ctx = c.getContext('2d')
  if (ctx) {
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.font = 'italic 900 120px Roboto, system-ui, sans-serif'
    const label = text.toUpperCase()
    ctx.lineJoin = 'round'
    ctx.lineWidth = 14
    ctx.strokeStyle = 'rgba(20,10,0,0.9)'
    ctx.strokeText(label, 512, 100)
    const g = ctx.createLinearGradient(0, 40, 0, 160)
    g.addColorStop(0, '#fffbe6')
    g.addColorStop(0.45, '#fcd34d')
    g.addColorStop(1, '#b45309')
    ctx.fillStyle = g
    ctx.fillText(label, 512, 100)
  }
  const tex = new THREE.CanvasTexture(c)
  tex.colorSpace = THREE.SRGBColorSpace
  return tex
}

function bandTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas')
  c.width = 512
  c.height = 64
  const ctx = c.getContext('2d')
  if (ctx) {
    const g = ctx.createLinearGradient(0, 0, 512, 0)
    g.addColorStop(0, 'rgba(10,6,24,0)')
    g.addColorStop(0.18, 'rgba(10,6,24,0.82)')
    g.addColorStop(0.82, 'rgba(10,6,24,0.82)')
    g.addColorStop(1, 'rgba(10,6,24,0)')
    ctx.fillStyle = g
    ctx.fillRect(0, 0, 512, 64)
    const edge = ctx.createLinearGradient(0, 0, 512, 0)
    edge.addColorStop(0, 'rgba(253,230,138,0)')
    edge.addColorStop(0.5, 'rgba(253,230,138,1)')
    edge.addColorStop(1, 'rgba(253,230,138,0)')
    ctx.fillStyle = edge
    ctx.fillRect(0, 0, 512, 3)
    ctx.fillRect(0, 61, 512, 3)
  }
  return new THREE.CanvasTexture(c)
}

/**
 * "Chain" banner across the middle of the overlay: the band opens, the word
 * slides in, a light sweeps across, then both slide out.
 */
export function chainBannerFx(
  host: ChainStackHost,
  text: string,
  seconds: number,
): { update: (dt: number) => boolean; dispose: () => void } {
  const { w, h } = host.size()
  const bandH = Math.max(48, Math.min(h * 0.17, 150))
  const textW = Math.min(w * 0.72, bandH * 5.6)
  const textH = textW * (192 / 1024)
  const cx = w / 2
  const cy = h / 2

  const bandTex = bandTexture()
  const bandGeo = new THREE.PlaneGeometry(w, bandH)
  const bandMat = new THREE.MeshBasicMaterial({
    map: bandTex,
    transparent: true,
    opacity: 0,
    depthWrite: false,
    depthTest: false,
  })
  const band = new THREE.Mesh(bandGeo, bandMat)
  band.position.set(cx, cy, 30)
  band.renderOrder = 90

  const textTex = bannerTexture(text)
  const textGeo = new THREE.PlaneGeometry(textW, textH)
  const textMat = new THREE.MeshBasicMaterial({
    map: textTex,
    transparent: true,
    opacity: 0,
    depthWrite: false,
    depthTest: false,
  })
  const label = new THREE.Mesh(textGeo, textMat)
  label.position.set(cx, cy, 31)
  label.renderOrder = 92

  const sweepGeo = new THREE.PlaneGeometry(bandH * 0.5, bandH * 1.1)
  const sweepMat = new THREE.MeshBasicMaterial({
    color: GOLD,
    transparent: true,
    opacity: 0,
    depthWrite: false,
    depthTest: false,
    blending: THREE.AdditiveBlending,
  })
  const sweep = new THREE.Mesh(sweepGeo, sweepMat)
  sweep.rotation.z = -0.35
  sweep.position.set(cx - w / 2, cy, 32)
  sweep.renderOrder = 93

  host.scene.add(band, label, sweep)
  const life = Math.max(0.4, seconds)
  const inT = Math.min(0.22, life * 0.25)
  const outT = Math.min(0.26, life * 0.3)
  const slide = w * 0.06
  const reduced = host.reduced()
  let age = 0
  return {
    update: (dt) => {
      age += dt
      const open = clamp01(age / inT)
      const close = clamp01((age - (life - outT)) / outT)
      const kIn = easeOutCubic(open)
      const alpha = kIn * (1 - close)
      band.scale.set(1, Math.max(0.01, kIn), 1)
      bandMat.opacity = alpha
      textMat.opacity = alpha
      const dx = reduced ? 0 : (1 - kIn) * -slide + close * slide
      label.position.x = cx + dx
      label.scale.setScalar(reduced ? 1 : lerp(1.18, 1, kIn))
      const sweepT = clamp01((age - inT) / Math.max(0.1, life - inT - outT))
      sweep.position.x = lerp(cx - textW * 0.6, cx + textW * 0.6, sweepT)
      sweepMat.opacity = reduced ? 0 : Math.sin(sweepT * Math.PI) * 0.45 * (1 - close)
      return age < life
    },
    dispose: () => {
      host.scene.remove(band, label, sweep)
      bandGeo.dispose()
      bandMat.dispose()
      bandTex.dispose()
      textGeo.dispose()
      textMat.dispose()
      textTex.dispose()
      sweepGeo.dispose()
      sweepMat.dispose()
    },
  }
}
